// Lazy routes are loaded from content-hashed chunks (see Loadable/lazy usage in routes/MainRoutes).
// A deploy replaces those files, so a tab that was open across the deploy -- or one holding a
// cached index.html -- asks for a chunk that is no longer on the server and the navigation dies
// with "Failed to fetch dynamically imported module". Reloading picks up the current index.html
// and the chunk names that go with it.

const RELOAD_MARKER = 'weightrax:chunk-reload-at';

// Long enough that a reload has had time to fail again if the chunk is genuinely unreachable
// (a bad deploy, an offline client), rather than merely stale.
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Whether a chunk load failure should be answered with a reload. Returns false when a reload was
 * already attempted moments ago, so a chunk that cannot be fetched at all does not put the page
 * into a refresh loop.
 */
export function shouldReloadForChunkError(now: number = Date.now()): boolean {
  let lastAttempt = 0;

  try {
    lastAttempt = Number(window.sessionStorage.getItem(RELOAD_MARKER)) || 0;
  } catch {
    // Storage can be unavailable (private browsing, blocked cookies). Without a marker we cannot
    // tell a first failure from a repeat, so reload once and accept that we may do it again.
    return true;
  }

  if (now - lastAttempt < RELOAD_COOLDOWN_MS) {
    return false;
  }

  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(now));
  } catch {
    // Ignore: the reload itself is still worth attempting.
  }

  return true;
}

/**
 * Listens for Vite's preload failures and reloads once to pick up the current build.
 */
export function registerChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Suppress the default unhandled rejection; we are handling it by reloading.
    event.preventDefault();

    if (shouldReloadForChunkError()) {
      window.location.reload();
    }
  });
}
